import {
  dishDayLabel,
  formatColumn,
  formatQuantity,
} from './order-proposal-labels'
import { isSingleDay } from './order-proposal-math'
import type {
  DateColumn,
  ProposalRow,
  ProposalView,
} from './order-proposal-math'

type Translate = (key: string, options?: Record<string, unknown>) => string

export interface OrderProposalCsvMeta {
  restaurantName: string
  /** The order days: the Liefertag, or a Liefertag range. */
  delivery: DateColumn
  columns: Array<DateColumn>
  /** The Hauptgruppe's name; null for all. */
  mainCategoryName: string | null
  showTagesgerichte: boolean
  hideZeroRows: boolean
}

/**
 * The Bestellvorschlag as CSV lines (CHG-2026-10-bestellvorschlag, D12): header lines for Standort,
 * Liefertag, the date columns and the filters, then one line per row on screen — the "verkauft"
 * dishes even while collapsed — with the Bestellung only when `orders` is given (order permission).
 */
export function orderProposalCsvLines(
  t: Translate,
  view: ProposalView,
  meta: OrderProposalCsvMeta,
  orders: Record<string, string> | null,
): Array<Array<string>> {
  const q = (value: number | null | undefined) =>
    value == null ? '' : formatQuantity(value)
  const order = (key: string) => (orders?.[key] ?? '').replace('.', ',')

  const lines: Array<Array<string>> = [
    [t('orderProposal.title')],
    [t('orderProposal.location'), meta.restaurantName],
    [t('orderProposal.deliveryDay'), formatColumn(meta.delivery, true)],
    ...meta.columns.map((column) => [
      t('orderProposal.compareDate'),
      formatColumn(column, true),
    ]),
    [
      t('orderProposal.mainCategory'),
      meta.mainCategoryName ?? t('orderProposal.allMainCategories'),
    ],
    [
      t('orderProposal.tagesgerichte'),
      meta.showTagesgerichte ? t('orderProposal.on') : t('orderProposal.off'),
    ],
    [
      t('orderProposal.hideZeroRows'),
      meta.hideZeroRows ? t('orderProposal.on') : t('orderProposal.off'),
    ],
    [],
  ]

  const header = [
    t('orderProposal.group'),
    t('orderProposal.columns.article'),
    t('orderProposal.columns.sku'),
    t('orderProposal.columns.onHand'),
    t('orderProposal.columns.incoming'),
    t('orderProposal.columns.total'),
  ]
  meta.columns.forEach((column) => {
    header.push(t('orderProposal.columns.sales'))
    if (isSingleDay(column)) {
      header.push(t('orderProposal.columns.proposal'))
    } else {
      header.push(
        t('orderProposal.columns.average'),
        t('orderProposal.columns.proposalSum'),
        t('orderProposal.columns.proposalAvg'),
      )
    }
  })
  if (orders) header.push(t('orderProposal.columns.order'))
  lines.push(header)

  const proposalLine = (group: string, row: ProposalRow) => {
    const cells = [
      group,
      row.name,
      row.sku,
      q(row.onHand),
      q(row.incoming),
      q(row.gesamt),
    ]
    row.columns.forEach((figures, i) => {
      cells.push(q(figures.sale))
      if (isSingleDay(meta.columns[i])) cells.push(q(figures.proposal))
      else
        cells.push(
          q(figures.average),
          q(figures.proposal),
          q(figures.proposalAvg),
        )
    })
    if (orders) cells.push(order(row.key))
    return cells
  }

  if (view.showTagesgerichte) {
    const toOrder = t('orderProposal.groups.toOrder')
    for (const row of view.planned) lines.push(proposalLine(toOrder, row))

    const sold = t('orderProposal.groups.sold')
    const totalCells = [
      sold,
      t('orderProposal.allTagesgerichte'),
      '',
      '',
      '',
      '',
    ]
    view.soldTotal.columns.forEach((column, i) => {
      totalCells.push(q(column.sale))
      if (isSingleDay(meta.columns[i])) totalCells.push('')
      else totalCells.push(q(column.average), '', '')
    })
    if (orders) totalCells.push('')
    lines.push(totalCells)

    for (const dish of view.sold) {
      const label = dishDayLabel(t, dish)
      const cells = [
        sold,
        label ? `${dish.name} (${label})` : dish.name,
        dish.sku,
        q(dish.onHand),
        q(dish.incoming),
        q(dish.gesamt),
      ]
      dish.sales.forEach((sale, i) => {
        cells.push(q(sale))
        if (isSingleDay(meta.columns[i])) cells.push('')
        else cells.push('', '', '')
      })
      if (orders) cells.push('')
      lines.push(cells)
    }
  }

  for (const group of view.groups) {
    const name = group.name ?? t('orderProposal.noCategory')
    for (const row of group.rows) lines.push(proposalLine(name, row))
  }
  return lines
}
